import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { MessageAction, MessageDeliveryStatus } from './constants';
import { DebugLogger } from './DebugLogger';
import { Logger } from './Logger';
import { isChoreographerSuppressed } from './utils';

// This timeout is used for any long-running messages that are either stuck for some reason, or that the user has yet to clear, to ensure that messaging doesn't stay blocked indefinitely
var CHOREOGRAPHER_MESSAGE_TTL = 300000; // 5 minute TTL for messages

// Lazily instantiate this the first time it's requested
var singleton;

/**
 * Choreographer Core API. Used to ensure only a single message at a time can be started on screen for the current user experience.
 * Accepts any number of plugins that want to register for choreographer services, provided they adhere to the IChoreographerPlugin contract.
 */
export var Choreographer = /*#__PURE__*/function () {
  function Choreographer() {
    _classCallCheck(this, Choreographer);
    this.state = {
      currentMessage: null,
      isDisabled: false,
      messages: new Map(),
      messageQueue: [],
      messageTtl: null,
      plugins: new Map(),
      queueingBehindMessage: null,
      shouldQueueMessages: false
    };
  }
  return _createClass(Choreographer, [{
    key: "stopCurrentMessage",
    value: function stopCurrentMessage() {
      var _this = this;
      if (this.state.currentMessage) {
        void this.stopMessageInternal(this.state.currentMessage.productId, this.state.currentMessage.messageId, this.state.currentMessage.analyticsClient, this.state.currentMessage.metricsData, this.state.currentMessage.plugin, true).then(function () {
          // Manually clear the message TTL and reenable plugins here just in case the unregistered plugin's message denied the
          // stop request. We may still get a collision on-screen, but we're considering the message forcibly stopped at this
          // point, so need to allow new requests.
          _this.clearMessageTtl();
          _this.enablePlugins();
        });
      }
    }

    /**
     * Enables the message queue, and logs which message everybody else is queueing up behind.
     *
     * @param productId Unique identifier for the product making the current startMessage request blocking others.
     * @param messageId Unique identifier for the messageId blocking others.
     */
  }, {
    key: "queueMessages",
    value: function queueMessages(productId, messageId) {
      this.state.shouldQueueMessages = true;
      this.state.queueingBehindMessage = {
        productId: productId,
        messageId: messageId,
        timestamp: Date.now()
      };
    }

    /**
     * Pushes all data associated with a startMessage request onto an internal queue, to potentially be processed at a later time.
     * If a pending message request opts out (or otherwise fails to start), this data will be used to reinitiate the startMessage
     * request for this message request instance. Returns a Promise that the caller can await as normal, and which will eventually
     * either be resolved as 'blocked' if any prior pending message is started, or will be resolved with its eventual outcome when
     * its startMessage request is processed.
     *
     * @param productId Unique identifier for the product making the current startMessage request being queued.
     * @param messageId Unique identifier for the messageId being queued.
     * @param analyticsClient The analytics client to be used for logging/metrics.
     * @param metricsData Additional data from plugin to be sent for metrics logging.
     * @param [plugin] An optional plugin instance.
     * @returns A Promise that resolves to a MessageDeliveryStatus value, to denote the ultimate status of the attempted message start operation.
     */
  }, {
    key: "queueMessage",
    value: function queueMessage(productId, messageId, analyticsClient, metricsData, plugin) {
      var _this2 = this;
      return new Promise(function (resolve) {
        _this2.state.messageQueue.push({
          productId: productId,
          messageId: messageId,
          analyticsClient: analyticsClient,
          metricsData: metricsData,
          plugin: plugin,
          resolve: resolve
        });
      });
    }

    /**
     * Responsible for pulling a previously queued message request that was received while a message request was already in flight.
     * Shift the first message off the top of the queue, and use the stored resolve function to kickstart another pass through the
     * startMessage function, ultimately resolving with the final disposition of the request. Set the internal state for shouldQueueMessages
     * to false if we're restarting a startMessage request, so that this doesn't get immediately thrown back into the queue. If it
     * makes it through to its start callback being fired, that code will turn the shouldQueueMessages state back on.
     */
  }, {
    key: "processQueuedMessage",
    value: function processQueuedMessage() {
      var queuedMessage = this.state.messageQueue.shift();

      // Disable the shouldQueueMessages state here. If we have a queued message, it will turn the shouldQueueMessages state back on when it
      // hits the call to its message's startCallback function. Otherwise, the queue is now empty, and we let things process normally.
      this.state.shouldQueueMessages = false;
      // Also null out the queueingBehindMessage value, which will be recreated if/when another message opens the queue below.
      this.state.queueingBehindMessage = null;
      if (queuedMessage) {
        queuedMessage.resolve(this.startMessage(queuedMessage.productId, queuedMessage.messageId, queuedMessage.analyticsClient, queuedMessage.metricsData, queuedMessage.plugin));
      }
    }

    /**
     * Responsible for flushing out any queued messages and resolving their Promises as "blocked". We don't officially consider a
     * message as "blocked" until one of the prior calls has signaled a valid "started" state. This way, any async callbacks that
     * are in flight can allow additional messages to queue up, and if they eventually opt out, we can move on to the next message
     * in the queue and see if it will signal success. Only when we get a successful start to we consider all queued up messages to
     * be "blocked". Run a loop to shift each of them off of the array and resolve them as blocked, until the array is empty.
     *
     * @param [plugin] An optional plugin instance
     */
  }, {
    key: "destroyMessageQueue",
    value: function destroyMessageQueue(plugin) {
      this.state.shouldQueueMessages = false;
      this.state.queueingBehindMessage = null;
      while (this.state.messageQueue.length > 0) {
        var _this$state$currentMe, _this$state$currentMe2, _this$state$currentMe3, _this$state$currentMe4;
        var _ref = this.state.messageQueue.shift(),
          analyticsClient = _ref.analyticsClient,
          messageId = _ref.messageId,
          metricsData = _ref.metricsData,
          productId = _ref.productId,
          resolve = _ref.resolve;
        var attributes = _objectSpread(_objectSpread({}, metricsData), {}, {
          productId: productId,
          messageId: messageId
        });
        var actionSubject = MessageAction.START;
        var logger = new Logger(actionSubject, messageId, attributes, analyticsClient, plugin);
        var debugLogger = new DebugLogger(productId, messageId);
        logger.blocked({
          blockedByProductId: (_this$state$currentMe = this.state.currentMessage) === null || _this$state$currentMe === void 0 ? void 0 : _this$state$currentMe.productId,
          blockedByMessageId: (_this$state$currentMe2 = this.state.currentMessage) === null || _this$state$currentMe2 === void 0 ? void 0 : _this$state$currentMe2.messageId
        });
        debugLogger.log({
          verbose: {
            phase: actionSubject,
            disposition: MessageDeliveryStatus.BLOCKED,
            blockedByProductId: (_this$state$currentMe3 = this.state.currentMessage) === null || _this$state$currentMe3 === void 0 ? void 0 : _this$state$currentMe3.productId,
            blockedByMessageId: (_this$state$currentMe4 = this.state.currentMessage) === null || _this$state$currentMe4 === void 0 ? void 0 : _this$state$currentMe4.messageId
          }
        });
        resolve(MessageDeliveryStatus.BLOCKED);
      }
    }

    /**
     * Sets a timeout to auto-expire any messaging that stays open for too long, to prevent messaging from being blocked indefinitely.
     *
     * @param productId Unique identifier for the product making the current startMessage request that triggered this message timeout.
     * @param messageId Unique identifier for the messageId that triggered this message timeout.
     * @param AnalyticsClient The analytics client to be used for logging/metrics.
     * @param metricsData Additional data from plugin to be sent for metrics logging.
     * @param [plugin] An optional plugin instance.
     */
  }, {
    key: "setMessageTtl",
    value: function setMessageTtl(productId, messageId, analyticsClient, metricsData, plugin) {
      var _this3 = this;
      this.state.currentMessage = {
        productId: productId,
        messageId: messageId,
        analyticsClient: analyticsClient,
        metricsData: metricsData,
        plugin: plugin,
        timestamp: Date.now()
      };
      this.state.messageTtl = setTimeout(function () {
        if (_this3.isCurrentMessage(productId, messageId)) {
          void _this3.stopCurrentMessage();
        }
      }, CHOREOGRAPHER_MESSAGE_TTL);
    }

    /**
     * Checks to see if the requesting productId/messageId pair matches the data for the currently-displayed message.
     *
     * @param productId Unique identifier for the product being checked for a current message match.
     * @param messageId Unique identifier for the messageId being checked for a current message match.
     */
  }, {
    key: "isCurrentMessage",
    value: function isCurrentMessage(productId, messageId) {
      return Boolean(this.state.currentMessage && this.state.currentMessage.productId === productId && this.state.currentMessage.messageId === messageId);
    }

    /**
     * Returns the currentMessage object.
     */
  }, {
    key: "getCurrentMessage",
    value: function getCurrentMessage() {
      return this.state.currentMessage;
    }

    /**
     * Returns the queueingBehindMessage object.
     */
  }, {
    key: "getCurrentPendingMessage",
    value: function getCurrentPendingMessage() {
      return this.state.queueingBehindMessage;
    }

    /**
     * Clears the timeout that was established for the currently-displayed message.
     */
  }, {
    key: "clearMessageTtl",
    value: function clearMessageTtl() {
      this.state.currentMessage = null;
      if (this.state.messageTtl) {
        clearTimeout(this.state.messageTtl);
      }
    }

    /**
     * Disables all plugins that are currently registered with the choreographer, called when a startMessage call is being dispatched.
     *
     * @param [exceptFor] An optional plugin instance.
     */
  }, {
    key: "disablePlugins",
    value: function disablePlugins(exceptFor) {
      this.state.isDisabled = true;
      _toConsumableArray(this.state.plugins.values()).forEach(function (p) {
        if (p !== exceptFor) {
          p.disable();
        }
      });
    }

    /**
     * Enables all plugins that are currently registered with the choreographer, called when a matching stopMessage call is successful.
     */
  }, {
    key: "enablePlugins",
    value: function enablePlugins() {
      _toConsumableArray(this.state.plugins.values()).forEach(function (p) {
        return p.enable();
      });
      this.state.isDisabled = false;
    }

    /**
     * A callback used to remove a plugin from the list of subscribers for the choreographer service.
     *
     * @param productId A unique string representing the plugin to be unregistered from the choreographer API.
     */
  }, {
    key: "unregisterPlugin",
    value: function unregisterPlugin(productId) {
      var _this$state$currentMe5;
      // If we're unregistering this productId's plugin, and the currentMessage is from this productId, we need to stop it to clear the choreographer
      if (((_this$state$currentMe5 = this.state.currentMessage) === null || _this$state$currentMe5 === void 0 ? void 0 : _this$state$currentMe5.productId) === productId) {
        void this.stopCurrentMessage();
      }
      this.state.messages.delete(productId);
      this.state.plugins.delete(productId);
    }

    /**
     * Determines whether any message is currently being displayed, by checking its own status and optionally, asking each registered plugin for its individual status.
     *
     * @returns A value reflecting whether a message is currently being displayed on screen.
     */
  }, {
    key: "isDisplayingMessage",
    value: function isDisplayingMessage() {
      return Boolean(this.state.currentMessage || _toConsumableArray(this.state.plugins.values()).reduce(function (isDisplayingMessage, p) {
        return isDisplayingMessage || p.isDisplayingMessage();
      }, false));
    }

    /**
     * This function is intended for test cleanup purposes only, *not* to be called by external callers.
     *
     * @ignore
     * @param productId Unique identifier for the product whose messages are being requested
     * @returns A Map of IMessage objects, keyed by unique messageId strings.
     */
  }, {
    key: "getMessages",
    value: function getMessages(productId) {
      var _this$state$messages$;
      return (_this$state$messages$ = this.state.messages.get(productId)) !== null && _this$state$messages$ !== void 0 ? _this$state$messages$ : new Map();
    }

    /**
     * Requests to start a message via the choreographer service, ensuring only a single message instance at a time on the screen.
     * A successful request will block any other message attempts until such time as the matching stopMessage request is received,
     * or a message TTL timeout has expired.
     *
     * @param productId Unique identifier for the product making the current startMessage request that triggered this message timeout.
     * @param messageId Unique identifier for the messageId that triggered this message timeout.
     * @param analyticsClient The analytics client to be used for logging/metrics.
     * @param [metricsData] Additional data from plugin to be sent for metrics logging.
     * @param [plugin] An optional plugin instance.
     * @returns A Promise that resolves to a MessageDeliveryStatus value, to denote the ultimate status of the attempted message start operation.
     */
  }, {
    key: "startMessage",
    value: (function () {
      var _startMessage = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(productId, messageId, analyticsClient) {
        var metricsData,
          plugin,
          _yield$message$start,
          _message$start,
          message,
          didStart,
          _message$onStart,
          debugLogger,
          actionSubject,
          requestTimestamp,
          attributes,
          logger,
          _yield$_message$start,
          _message$start2,
          _this$state$queueingB,
          _this$state$queueingB2,
          _this$state$currentMe6,
          _this$state$currentMe7,
          _this$state$currentMe8,
          _this$state$currentMe9,
          _message,
          _didStart,
          _message$onStart2,
          e,
          _args = arguments;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              metricsData = _args.length > 3 && _args[3] !== undefined ? _args[3] : {};
              plugin = _args.length > 4 ? _args[4] : undefined;
              if (!isChoreographerSuppressed()) {
                _context.next = 22;
                break;
              }
              if (!this.hasMessageRecord(productId, messageId)) {
                _context.next = 21;
                break;
              }
              message = this.ensureMessageRecord(productId, messageId);
              _context.next = 7;
              return (_message$start = message.start) === null || _message$start === void 0 ? void 0 : _message$start.call(message);
            case 7:
              _context.t1 = _yield$message$start = _context.sent;
              _context.t0 = _context.t1 !== null;
              if (!_context.t0) {
                _context.next = 11;
                break;
              }
              _context.t0 = _yield$message$start !== void 0;
            case 11:
              if (!_context.t0) {
                _context.next = 15;
                break;
              }
              _context.t2 = _yield$message$start;
              _context.next = 16;
              break;
            case 15:
              _context.t2 = true;
            case 16:
              didStart = _context.t2;
              if (!didStart) {
                _context.next = 20;
                break;
              }
              // Fire the onStart callback, if provided, to handle any post-start concerns for the consumer
              (_message$onStart = message.onStart) === null || _message$onStart === void 0 || _message$onStart.call(message);
              return _context.abrupt("return", MessageDeliveryStatus.STARTED);
            case 20:
              return _context.abrupt("return", MessageDeliveryStatus.CANCELED);
            case 21:
              return _context.abrupt("return", MessageDeliveryStatus.UNREGISTERED);
            case 22:
              debugLogger = new DebugLogger(productId, messageId);
              actionSubject = MessageAction.START;
              requestTimestamp = performance.now();
              debugLogger.log({
                info: {
                  phase: actionSubject,
                  timestamp: requestTimestamp
                },
                verbose: {
                  phase: actionSubject,
                  timestamp: requestTimestamp
                }
              });
              attributes = _objectSpread(_objectSpread({}, metricsData), {}, {
                productId: productId,
                messageId: messageId
              });
              logger = new Logger(actionSubject, messageId, attributes, analyticsClient, plugin);
              _context.prev = 28;
              if (this.hasMessageRecord(productId, messageId)) {
                _context.next = 33;
                break;
              }
              // Bail early here, before even checking if we're in a queueing situation, or blocked, or anything else
              logger.unregistered();
              debugLogger.log({
                verbose: {
                  phase: actionSubject,
                  disposition: MessageDeliveryStatus.UNREGISTERED
                }
              });
              return _context.abrupt("return", MessageDeliveryStatus.UNREGISTERED);
            case 33:
              if (!this.state.shouldQueueMessages) {
                _context.next = 36;
                break;
              }
              debugLogger.log({
                verbose: {
                  phase: actionSubject,
                  disposition: "queued behind ".concat((_this$state$queueingB = this.state.queueingBehindMessage) === null || _this$state$queueingB === void 0 ? void 0 : _this$state$queueingB.productId, " ").concat((_this$state$queueingB2 = this.state.queueingBehindMessage) === null || _this$state$queueingB2 === void 0 ? void 0 : _this$state$queueingB2.messageId)
                }
              });
              return _context.abrupt("return", this.queueMessage(productId, messageId, analyticsClient, metricsData, plugin));
            case 36:
              if (!(this.state.isDisabled || this.isDisplayingMessage())) {
                _context.next = 40;
                break;
              }
              logger.blocked({
                blockedByProductId: (_this$state$currentMe6 = this.state.currentMessage) === null || _this$state$currentMe6 === void 0 ? void 0 : _this$state$currentMe6.productId,
                blockedByMessageId: (_this$state$currentMe7 = this.state.currentMessage) === null || _this$state$currentMe7 === void 0 ? void 0 : _this$state$currentMe7.messageId
              });
              debugLogger.log({
                verbose: {
                  phase: actionSubject,
                  disposition: MessageDeliveryStatus.BLOCKED,
                  blockedByProductId: (_this$state$currentMe8 = this.state.currentMessage) === null || _this$state$currentMe8 === void 0 ? void 0 : _this$state$currentMe8.productId,
                  blockedByMessageId: (_this$state$currentMe9 = this.state.currentMessage) === null || _this$state$currentMe9 === void 0 ? void 0 : _this$state$currentMe9.messageId
                }
              });
              return _context.abrupt("return", MessageDeliveryStatus.BLOCKED);
            case 40:
              _message = this.ensureMessageRecord(productId, messageId);
              this.queueMessages(productId, messageId);
              _context.next = 44;
              return (_message$start2 = _message.start) === null || _message$start2 === void 0 ? void 0 : _message$start2.call(_message);
            case 44:
              _context.t4 = _yield$_message$start = _context.sent;
              _context.t3 = _context.t4 !== null;
              if (!_context.t3) {
                _context.next = 48;
                break;
              }
              _context.t3 = _yield$_message$start !== void 0;
            case 48:
              if (!_context.t3) {
                _context.next = 52;
                break;
              }
              _context.t5 = _yield$_message$start;
              _context.next = 53;
              break;
            case 52:
              _context.t5 = true;
            case 53:
              _didStart = _context.t5;
              if (!_didStart) {
                _context.next = 62;
                break;
              }
              // Fire the onStart callback, if provided, to handle any post-start concerns for the consumer
              (_message$onStart2 = _message.onStart) === null || _message$onStart2 === void 0 || _message$onStart2.call(_message);
              logger.started();
              this.disablePlugins(plugin);
              this.setMessageTtl(productId, messageId, analyticsClient, metricsData);
              // Successfully started the message, flush the queue and mark any pending requests as "blocked"
              this.destroyMessageQueue(plugin);
              debugLogger.log({
                verbose: {
                  phase: actionSubject,
                  disposition: MessageDeliveryStatus.STARTED
                }
              });
              return _context.abrupt("return", MessageDeliveryStatus.STARTED);
            case 62:
              // Check to see if we have any pending requests in the message queue and attempt to start the next one in line
              this.processQueuedMessage();
              debugLogger.log({
                verbose: {
                  phase: actionSubject,
                  disposition: MessageDeliveryStatus.CANCELED
                }
              });
              return _context.abrupt("return", MessageDeliveryStatus.CANCELED);
            case 67:
              _context.prev = 67;
              _context.t6 = _context["catch"](28);
              e = _context.t6;
              logger.error({
                reason: e.message
              });
              debugLogger.log({
                verbose: {
                  phase: actionSubject,
                  disposition: MessageDeliveryStatus.ERROR
                }
              });
              return _context.abrupt("return", MessageDeliveryStatus.ERROR);
            case 73:
            case "end":
              return _context.stop();
          }
        }, _callee, this, [[28, 67]]);
      }));
      function startMessage(_x, _x2, _x3) {
        return _startMessage.apply(this, arguments);
      }
      return startMessage;
    }()
    /**
     * Requests to stop a message via the choreographer service. A successful request will allow any other plugins to begin issuing startMessage requests again.
     * This version of the stopMessage function is private, and only intended to be called internally, so that it can make use of the forceStop parameter without exposing that to external callers.
     *
     * @param productId Unique identifier for the product making the current stopMessage request.
     * @param messageId Unique identifier for the messageId to be stopped.
     * @param analyticsClient The analytics client to be used for logging/metrics.
     * @param [metricsData] Additional data from plugin to be sent for metrics logging.
     * @param [plugin] An optional plugin instance.
     * @param [forceStop] A boolean to denote whether to forcefully clear the Choreographer's blocked state (used for TTL expiration and unregistering a plugin).
     * @returns A Promise that resolves to a MessageDeliveryStatus value, to denote the ultimate status of the attempted message stop operation.
     */
    )
  }, {
    key: "stopMessageInternal",
    value: (function () {
      var _stopMessageInternal = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(productId, messageId, analyticsClient) {
        var metricsData,
          plugin,
          forceStop,
          _yield$message$stop,
          _message$stop,
          message,
          didStop,
          _message$onStop,
          debugLogger,
          actionSubject,
          requestTimestamp,
          attributes,
          logger,
          _yield$_message2$stop,
          _message2$stop,
          _message2,
          _didStop,
          _message2$onStop,
          e,
          _args2 = arguments;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              metricsData = _args2.length > 3 && _args2[3] !== undefined ? _args2[3] : {};
              plugin = _args2.length > 4 ? _args2[4] : undefined;
              forceStop = _args2.length > 5 && _args2[5] !== undefined ? _args2[5] : false;
              if (!isChoreographerSuppressed()) {
                _context2.next = 23;
                break;
              }
              if (!this.hasMessageRecord(productId, messageId)) {
                _context2.next = 22;
                break;
              }
              message = this.ensureMessageRecord(productId, messageId);
              _context2.next = 8;
              return (_message$stop = message.stop) === null || _message$stop === void 0 ? void 0 : _message$stop.call(message);
            case 8:
              _context2.t1 = _yield$message$stop = _context2.sent;
              _context2.t0 = _context2.t1 !== null;
              if (!_context2.t0) {
                _context2.next = 12;
                break;
              }
              _context2.t0 = _yield$message$stop !== void 0;
            case 12:
              if (!_context2.t0) {
                _context2.next = 16;
                break;
              }
              _context2.t2 = _yield$message$stop;
              _context2.next = 17;
              break;
            case 16:
              _context2.t2 = true;
            case 17:
              didStop = _context2.t2;
              if (!didStop) {
                _context2.next = 21;
                break;
              }
              // Fire the onStop callback, if provided, to handle any post-stop concerns for the consumer
              (_message$onStop = message.onStop) === null || _message$onStop === void 0 || _message$onStop.call(message);
              return _context2.abrupt("return", MessageDeliveryStatus.STOPPED);
            case 21:
              return _context2.abrupt("return", MessageDeliveryStatus.CANCELED);
            case 22:
              return _context2.abrupt("return", MessageDeliveryStatus.UNREGISTERED);
            case 23:
              debugLogger = new DebugLogger(productId, messageId);
              actionSubject = MessageAction.STOP;
              requestTimestamp = performance.now();
              debugLogger.log({
                info: {
                  phase: actionSubject,
                  timestamp: requestTimestamp
                },
                verbose: {
                  phase: actionSubject,
                  timestamp: requestTimestamp
                }
              });
              attributes = _objectSpread(_objectSpread({}, metricsData), {}, {
                productId: productId,
                messageId: messageId
              });
              logger = new Logger(actionSubject, messageId, attributes, analyticsClient, plugin);
              _context2.prev = 29;
              if (this.hasMessageRecord(productId, messageId)) {
                _context2.next = 34;
                break;
              }
              logger.unregistered();
              debugLogger.log({
                verbose: {
                  phase: actionSubject,
                  disposition: MessageDeliveryStatus.UNREGISTERED
                }
              });
              return _context2.abrupt("return", MessageDeliveryStatus.UNREGISTERED);
            case 34:
              _message2 = this.ensureMessageRecord(productId, messageId); // Enable the message queue *if* the current stopMessage request matches the currentMessage data.
              // Any incoming startMessage requests will be queued while this stopMessage request is in-flight,
              // so that they can be immediately honored if this request comes back successfully.
              if (this.isCurrentMessage(productId, messageId)) {
                this.queueMessages(productId, messageId);
              }
              _context2.next = 38;
              return (_message2$stop = _message2.stop) === null || _message2$stop === void 0 ? void 0 : _message2$stop.call(_message2);
            case 38:
              _context2.t4 = _yield$_message2$stop = _context2.sent;
              _context2.t3 = _context2.t4 !== null;
              if (!_context2.t3) {
                _context2.next = 42;
                break;
              }
              _context2.t3 = _yield$_message2$stop !== void 0;
            case 42:
              if (!_context2.t3) {
                _context2.next = 46;
                break;
              }
              _context2.t5 = _yield$_message2$stop;
              _context2.next = 47;
              break;
            case 46:
              _context2.t5 = true;
            case 47:
              _didStop = _context2.t5;
              if (!(_didStop || forceStop)) {
                _context2.next = 53;
                break;
              }
              if (this.isCurrentMessage(productId, messageId)) {
                // Fire the onStop callback, if provided, to handle any post-stop concerns for the consumer
                (_message2$onStop = _message2.onStop) === null || _message2$onStop === void 0 || _message2$onStop.call(_message2);
                // Only log this even if we're stopping the current message, so we have a 1:1 mapping of starts to stops.
                // If this is a forced stop, log that in the attributes, so we can track those events specifically.
                // Also log the value of didStop, so we know if this was a rejected stop attempt that *had* to be force stopped.
                logger.stopped(forceStop ? {
                  forceStop: forceStop,
                  didStop: _didStop
                } : undefined);
                // Only clear the message TTL and reenable plugins if we're stopping the *current* message
                this.clearMessageTtl();
                this.enablePlugins();
              }

              // Check to see if we have any pending requests in the message queue and attempt to start them
              this.processQueuedMessage();
              debugLogger.log({
                verbose: {
                  phase: actionSubject,
                  disposition: MessageDeliveryStatus.STOPPED
                }
              });
              return _context2.abrupt("return", MessageDeliveryStatus.STOPPED);
            case 53:
              // Stopping the message failed, so the UI is still intentionally blocked, flush the queue and mark any pending requests as "blocked"
              this.destroyMessageQueue(plugin);
              debugLogger.log({
                verbose: {
                  phase: actionSubject,
                  disposition: MessageDeliveryStatus.CANCELED
                }
              });
              return _context2.abrupt("return", MessageDeliveryStatus.CANCELED);
            case 58:
              _context2.prev = 58;
              _context2.t6 = _context2["catch"](29);
              e = _context2.t6;
              logger.error({
                reason: e.message
              });
              debugLogger.log({
                verbose: {
                  phase: actionSubject,
                  disposition: MessageDeliveryStatus.ERROR
                }
              });
              return _context2.abrupt("return", MessageDeliveryStatus.ERROR);
            case 64:
            case "end":
              return _context2.stop();
          }
        }, _callee2, this, [[29, 58]]);
      }));
      function stopMessageInternal(_x4, _x5, _x6) {
        return _stopMessageInternal.apply(this, arguments);
      }
      return stopMessageInternal;
    }()
    /**
     * Requests to stop a message via the choreographer service. A successful request will allow any other plugins to begin issuing startMessage requests again.
     *
     * @param productId Unique identifier for the product making the current stopMessage request.
     * @param messageId Unique identifier for the messageId to be stopped.
     * @param analyticsClient The analytics client to be used for logging/metrics.
     * @param [metricsData] Additional data from plugin to be sent for metrics logging.
     * @param [plugin] An optional plugin instance.
     * @returns A Promise that resolves to a MessageDeliveryStatus value, to denote the ultimate status of the attempted message stop operation.
     */
    )
  }, {
    key: "stopMessage",
    value: (function () {
      var _stopMessage = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(productId, messageId, analyticsClient) {
        var metricsData,
          plugin,
          _args3 = arguments;
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              metricsData = _args3.length > 3 && _args3[3] !== undefined ? _args3[3] : {};
              plugin = _args3.length > 4 ? _args3[4] : undefined;
              return _context3.abrupt("return", this.stopMessageInternal(productId, messageId, analyticsClient, metricsData, plugin));
            case 3:
            case "end":
              return _context3.stop();
          }
        }, _callee3, this);
      }));
      function stopMessage(_x7, _x8, _x9) {
        return _stopMessage.apply(this, arguments);
      }
      return stopMessage;
    }()
    /**
     * Registers a plugin instance with the choreographer API. Allows the choreographer to enable and disable it as needed to ensure individual message experiences on screen.
     *
     * @param productId Unique identifier to register as the product owner of this plugin.
     * @param plugin A plugin instance to be registered with the choreographer instance.
     * @returns A callback to be used to unregister this plugin with the choreographer instance, for cleanup purposes, if necessary.
     */
    )
  }, {
    key: "registerPlugin",
    value: function registerPlugin(productId, plugin) {
      var _this4 = this;
      if (this.state.plugins.has(productId)) {
        throw new Error("Choreographer plugin already registered for ".concat(productId, "!"));
      }
      this.state.plugins = this.state.plugins.set(productId, plugin);
      if (this.state.isDisabled) {
        plugin.disable();
      }
      return function () {
        _this4.unregisterPlugin(productId);
      };
    }

    /**
     * Validates that a message record is present and ready to be retrieved and/or manipulated.
     * Creates the message instance if it doesn't yet exist, initializing both start and stop callbacks to noop functions.
     *
     * @param productId Unique identifier to retrieve map of IMessage objects.
     * @param messageId Unique identifier for the messageId to be retrieved.
     * @returns IMessage object for the requested productId/messageId combination.
     */
  }, {
    key: "ensureMessageRecord",
    value: function ensureMessageRecord(productId, messageId) {
      var productMessages = this.state.messages.get(productId);
      if (!productMessages) {
        productMessages = new Map();
        this.state.messages.set(productId, productMessages);
      }
      var message = productMessages.get(messageId);
      if (!message) {
        message = {};
        productMessages.set(messageId, message);
      }
      return message;
    }

    /**
     * Checks for the existance of a message record for a given productId/messageId combination.
     *
     * @param productId Unique identifier to check for a map of IMessage objects.
     * @param messageId Unique identifier to check for within the map of productId IMessage objects.
     * @returns Boolean indicating whether or not an IMessage record exists for the requested productId/messageId combination.
     */
  }, {
    key: "hasMessageRecord",
    value: function hasMessageRecord(productId, messageId) {
      var _this$state$messages$2, _this$state$messages$3;
      return Boolean((_this$state$messages$2 = this.state.messages.get(productId)) === null || _this$state$messages$2 === void 0 || (_this$state$messages$3 = _this$state$messages$2.has) === null || _this$state$messages$3 === void 0 ? void 0 : _this$state$messages$3.call(_this$state$messages$2, messageId));
    }

    /**
     * Subscribes callbacks to be used for starting and stopping message requests through the choreographer, keyed by a combination of productId and messageId.
     *
     * @param productId Unique identifier to be used as a primary key for this message subscription.
     * @param messageId Unique identifier within the context of all productId message entries, to be used as a secondary key for this message subscription.
     * @param options An object containing optional start and stop callback functions, to be invoked upon successful requests to startMessage and stopMessage, respectively.
     * @param finalizers An object containing optional onStart and onStop callback functions, to be invoked upon successful starts and stops of messages, respectively.
     */
  }, {
    key: "on",
    value: function on(productId, messageId) {
      var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      var finalizers = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
      var message = _objectSpread(_objectSpread(_objectSpread({}, this.ensureMessageRecord(productId, messageId)), options), finalizers);
      this.state.messages.get(productId).set(messageId, message);
    }

    /**
     * Subscribes a callback to be used for starting message requests through the choreographer, keyed by a combination of productId and messageId.
     *
     * @param productId Unique identifier to be used as a primary key for this message subscription.
     * @param messageId Unique identifier within the context of all productId message entries, to be used as a secondary key for this message subscription.
     * @param startCallback A callback to be invoked upon successful requests to startMessage.
     * @param finalizers An object containing optional onStart and onStop callback functions, to be invoked upon successful starts and stops of messages, respectively.
     */
  }, {
    key: "onStart",
    value: function onStart(productId, messageId, startCallback, finalizers) {
      this.on(productId, messageId, {
        start: startCallback
      }, finalizers);
    }

    /**
     * Subscribes a callback to be used for stopping message requests through the choreographer, keyed by a combination of productId and messageId.
     *
     * @param productId Unique identifier to be used as a primary key for this message subscription.
     * @param messageId Unique identifier within the context of all productId message entries, to be used as a secondary key for this message subscription.
     * @param stopCallback A callback to be invoked upon successful requests to stopMessage.
     * @param finalizers An object containing optional onStart and onStop callback functions, to be invoked upon successful starts and stops of messages, respectively.
     */
  }, {
    key: "onStop",
    value: function onStop(productId, messageId, stopCallback) {
      var finalizers = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
      this.on(productId, messageId, {
        stop: stopCallback
      }, finalizers);
    }

    /**
     * Unsubscribes a message's start and stop message callbacks from the choreographer, keyed by a combination of productId and messageId.
     *
     * @param productId Unique identifier to be used as a primary key for unsubscribing this message.
     * @param messageId Unique identifier within the context of all productId message entries, to be used as a secondary key for unsubscribing this message.
     */
  }, {
    key: "off",
    value: function off(productId, messageId) {
      var _this$state$messages$4, _this$state$messages$5;
      (_this$state$messages$4 = this.state.messages.get(productId)) === null || _this$state$messages$4 === void 0 || (_this$state$messages$5 = _this$state$messages$4.delete) === null || _this$state$messages$5 === void 0 || _this$state$messages$5.call(_this$state$messages$4, messageId);
    }
  }], [{
    key: "getInstance",
    value:
    /**
     * Static method to create and/or return a singleton of the choreographer API.
     *
     * @returns Shared instance of choreographer API.
     */
    function getInstance() {
      if (!singleton) {
        singleton = new this();
      }
      return singleton;
    }
  }]);
}();